import React, { useState } from 'react';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import MobileInput from '../ui/mobile-input';
import { validateMobile } from '../../utils/phoneValidation';
import { useToast } from '../../hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { isMobileContainer } from '../../utils/device';
import { cn } from '../../lib/utils';

const PRIMARY_BTN = 'bg-blue-600 hover:bg-blue-700 text-white';

function BranchForm({ form, setForm, phoneError, setPhoneError, saving, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <Label>Branch name *</Label>
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="mt-1.5 rounded-lg"
          placeholder="e.g. North Campus"
          required
        />
      </div>
      <div>
        <Label>Code</Label>
        <Input
          value={form.code}
          onChange={(e) => setForm({ ...form, code: e.target.value })}
          className="mt-1.5 rounded-lg"
          placeholder="Optional short code"
        />
      </div>
      <div>
        <Label>Address</Label>
        <Input
          value={form.address}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
          className="mt-1.5 rounded-lg"
          placeholder="Optional"
        />
      </div>
      <div className={cn('grid gap-4', isMobileContainer() ? 'grid-cols-1' : 'sm:grid-cols-2')}>
        <div>
          <Label>Phone</Label>
          <MobileInput
            value={form.phone}
            onChange={(v) => {
              setForm({ ...form, phone: v });
              setPhoneError('');
            }}
            error={phoneError}
            className="mt-1.5 rounded-lg"
          />
        </div>
        <div>
          <Label>Email</Label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="mt-1.5 rounded-lg"
            placeholder="Optional"
          />
        </div>
      </div>
      <Button type="submit" className={`w-full ${PRIMARY_BTN}`} disabled={saving}>
        {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating…</> : 'Create branch'}
      </Button>
    </form>
  );
}

export default function AddBranchDialog({ open, onOpenChange, onCreated }) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [form, setForm] = useState({
    name: '',
    code: '',
    address: '',
    phone: '',
    email: '',
  });

  const resetForm = () => {
    setForm({ name: '', code: '', address: '', phone: '', email: '' });
    setPhoneError('');
  };

  const handleOpenChange = (next) => {
    if (!next) resetForm();
    onOpenChange(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) {
      toast({ title: 'Branch name is required', variant: 'destructive' });
      return;
    }
    if (form.phone) {
      const err = validateMobile(form.phone);
      if (err) {
        setPhoneError(err);
        return;
      }
    }

    setSaving(true);
    try {
      const res = await api.post('/branches', {
        name,
        code: form.code.trim() || undefined,
        address: form.address.trim() || undefined,
        phone: form.phone || undefined,
        email: form.email.trim() || undefined,
      });
      const body = res.data;
      const branch = body?.data ?? body;
      toast({ title: 'Branch created', description: `${name} is ready to use.` });
      resetForm();
      onOpenChange(false);
      if (onCreated) onCreated(branch);
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to create branch';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const formEl = (
    <BranchForm
      form={form}
      setForm={setForm}
      phoneError={phoneError}
      setPhoneError={setPhoneError}
      saving={saving}
      onSubmit={handleSubmit}
    />
  );

  if (isMobileContainer()) {
    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-safe max-h-[90vh] overflow-y-auto">
          <SheetHeader className="text-left pb-4">
            <SheetTitle>Add branch</SheetTitle>
          </SheetHeader>
          {formEl}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="border-border text-foreground max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add branch</DialogTitle>
        </DialogHeader>
        {formEl}
      </DialogContent>
    </Dialog>
  );
}
